import { Button } from "@/components/Button";
import { UpdateKlaviyoListModal } from "@/components/Modal";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { tv } from "tailwind-variants";
import klaviyoLogo from "@/assets/images/collect/klaviyo-logo.png";

interface IProps {
  klaviyoLists?: IDatashipsChannelLists;
}
const styles = tv({
  slots: {
    tableWrapper: "w-full shadow-md",
    titleWrapper: "ml-20 mt-10 flex flex-row items-center gap-10",
    integrationLogo: "h-15 w-15",
    table: "w-full",
    thead: "border-t border-gray",
    th: "px-20 py-10 text-left",
    tbody: "",
    tr: "w-full border-y border-gray even:bg-gray-light/50",
    td: "px-20 py-10",
    emptyTable: "h-[200px] text-center",
    caretIcon: "h-15 w-15 text-secondary",
  },
});

const KlaviyoListsTable = ({ klaviyoLists }: IProps) => {
  const { t } = useTranslation();
  const [activeList, setActiveList] = useState<IKlaviyoList>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const {
    tableWrapper,
    titleWrapper,
    integrationLogo,
    table: tableContainer,
    thead,
    th,
    tbody,
    tr,
    td,
    emptyTable,
    caretIcon,
  } = styles();

  const handleUpdateModal = (list: IKlaviyoList) => () => {
    setActiveList(list);
    toggleModal();
  };

  const toggleModal = () => setIsModalOpen(isOpen => !isOpen);

  const renderModal = () => (
    <UpdateKlaviyoListModal
      list={activeList}
      isOpen={isModalOpen}
      closeModal={toggleModal}
    />
  );

  const renderTableHead = () => (
    <thead className={thead()} data-testid="table-head">
      <tr>
        <th className={th()} role="cell">
          {t("settings.integration.klaviyoLists.table.columns.type")}
        </th>
        <th className={th()} role="cell">
          {t("settings.integration.klaviyoLists.table.columns.name")}
        </th>
        <th className={th()} role="cell">
          {t("settings.integration.klaviyoLists.table.columns.id")}
        </th>
        <th className={th()} role="cell">
          {t("settings.integration.klaviyoLists.table.columns.actions")}
        </th>
      </tr>
    </thead>
  );

  const renderTableBody = () => {
    if (!klaviyoLists || klaviyoLists?.lists?.length === 0) {
      return (
        <tr data-testid="table-empty-container">
          <td className={emptyTable()} colSpan={4}>
            {t("settings.integration.klaviyoLists.emptyState")}
          </td>
        </tr>
      );
    }

    return klaviyoLists?.lists?.map(({ type, name, id }, i) => (
      <tr key={i} className={tr()} data-testid="table-row">
        <td className={td()} role="cell">
          {type}
        </td>
        <td className={td()} role="cell">
          {name ||
            t("settings.integration.klaviyoLists.table.missingList.name")}
        </td>
        <td className={td()} role="cell">
          {id || t("settings.integration.klaviyoLists.table.missingList.id")}
        </td>
        <td className={td()} role="cell">
          <Button
            as="button"
            variant="link"
            trackingId="control-panel.email.integration-settings.klaviyo-lists.open-list"
            onClick={handleUpdateModal({
              id,
              type,
              channel: klaviyoLists?.channel,
            })}>
            {!id
              ? t("settings.integration.klaviyoLists.table.cta.add")
              : t("settings.integration.klaviyoLists.table.cta.update")}
            <FontAwesomeIcon icon={faCaretRight} className={caretIcon()} />
          </Button>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <div className={tableWrapper()}>
        <div className={titleWrapper()}>
          <img src={klaviyoLogo} className={integrationLogo()} />
          <b>
            {t(
              klaviyoLists?.channel === "SMS"
                ? "settings.integration.klaviyoLists.table.smsTitle"
                : "settings.integration.klaviyoLists.table.emailTitle",
            )}
          </b>
        </div>
        <table role="table" className={tableContainer()}>
          {renderTableHead()}
          <tbody className={tbody()} data-testid="table-body">
            {renderTableBody()}
          </tbody>
        </table>
      </div>

      {renderModal()}
    </>
  );
};

export default KlaviyoListsTable;
